import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { money, shortDate } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { StatusBadge } from "../StatusBadge";

interface GiftDoc { id: string; code: string; buyer_name: string; recipient_email: string; recipient_name: string; message: string; amount_pence: number; status: string; created_at: string; redeemed_at?: string | null }
const presets = [2500, 5000, 10000];

export function GiftPanel({ balance, loaded = true }: { balance: number; loaded?: boolean }) {
  const qc = useQueryClient();
  const { data } = useQuery<{ sent: GiftDoc[]; received: GiftDoc[] }>({ queryKey: ["gifts"], queryFn: () => api.get("/gifts").then((r) => r.data) });
  const [form, setForm] = useState({ amount: 2500, email: "", name: "", message: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => ["gifts", "wallet", "ledger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gifts", { amount_pence: form.amount, recipient_email: form.email, recipient_name: form.name, message: form.message });
      toast.success(`Gift ${data.gift.code} emailed to ${form.email}`);
      setForm({ amount: 2500, email: "", name: "", message: "" });
      refresh();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code });
      toast.success(`${money(data.gift.amount_pence)} from ${data.gift.buyer_name} added to your cellar`);
      setCode("");
      refresh();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const cancel = async (g: GiftDoc) => {
    try { await api.post(`/gifts/${g.id}/cancel`); toast.success("Gift cancelled — credit returned to your cellar"); refresh(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="grid gap-12 lg:grid-cols-2" data-testid="gift-panel">
      <div>
        <div className="eyebrow">Give cellar credit</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">A gift they choose themselves</h2>
        <p className="mt-3 text-sm text-neutral-600">Drawn from your cellar balance ({money(balance)}). We email them a code; they redeem it into their own cellar and pick the bottles.</p>
        <div className="mt-6 grid grid-cols-3 gap-2">
          {presets.map((p) => <button key={p} onClick={() => setForm({ ...form, amount: p })} className={`border py-3 text-sm tabular-nums transition-colors duration-200 ${form.amount === p ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`gift-preset-${p}`}>{money(p)}</button>)}
        </div>
        <div className="mt-3 flex items-center gap-3"><span className="font-serif text-2xl">£</span><Input type="number" min={10} max={1000} value={form.amount / 100} onChange={(e) => setForm({ ...form, amount: Math.round(Number(e.target.value) * 100) })} className="rounded-none bg-transparent tabular-nums" data-testid="gift-amount-input" /></div>
        <div className="mt-4 space-y-3">
          <Input type="email" placeholder="Recipient's email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-transparent" data-testid="gift-email-input" />
          <Input placeholder="Recipient's name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent" data-testid="gift-name-input" />
          <Input placeholder="A message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none bg-transparent" data-testid="gift-message-input" />
        </div>
        <Button onClick={send} disabled={busy || !form.email || form.amount < 1000 || form.amount > balance} className="mt-5 w-full rounded-none py-6 text-base hover-lift" data-testid="gift-send-button">
          <Gift className="mr-2 h-4 w-4" strokeWidth={1.25} />{!loaded ? "Loading balance…" : form.amount > balance ? "Not enough cellar credit" : `Send ${money(form.amount)} gift`}
        </Button>
        {data && data.sent.length > 0 && (
          <div className="mt-8" data-testid="gifts-sent">
            <div className="eyebrow">Gifts you've sent</div>
            {data.sent.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-3 border-b border-border py-3 text-sm" data-testid={`gift-sent-${g.id}`}>
                <div>{g.recipient_email}<div className="text-xs text-neutral-500">{g.code} · {shortDate(g.created_at)}</div></div>
                <div className="flex items-center gap-3"><StatusBadge status={g.status} /><span className="tabular-nums">{money(g.amount_pence)}</span>{g.status === "sent" && <button onClick={() => cancel(g)} className="text-xs underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`gift-cancel-${g.id}`}>Cancel</button>}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="eyebrow">Received a code?</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Redeem into your cellar</h2>
        <div className="mt-6 flex gap-2">
          <Input placeholder="HW-XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="rounded-none bg-transparent font-mono tracking-widest" data-testid="gift-redeem-input" />
          <Button onClick={redeem} disabled={busy || code.length < 6} className="rounded-none px-6" data-testid="gift-redeem-button">Redeem</Button>
        </div>
        {data && data.received.length > 0 && (
          <div className="mt-8" data-testid="gifts-received">
            <div className="eyebrow">Gifts for you</div>
            {data.received.map((g) => (
              <div key={g.id} className="border-b border-border py-3 text-sm" data-testid={`gift-received-${g.id}`}>
                <div className="flex items-center justify-between gap-3"><span>From {g.buyer_name}</span><div className="flex items-center gap-3"><StatusBadge status={g.status} /><span className="tabular-nums">{money(g.amount_pence)}</span></div></div>
                {g.message && <p className="mt-1 font-serif italic text-neutral-600">“{g.message}”</p>}
                {g.status === "sent" && <button onClick={() => setCode(g.code)} className="mt-1 text-xs underline underline-offset-4" data-testid={`gift-use-code-${g.id}`}>Use code {g.code}</button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
